import { isNil } from 'lodash-es';
import CommonTruncatedList from '@/components/elements/CommonTruncatedList';
import { ColumnDef } from '@/components/elements/table/types';
import ReferralStatusChip from '@/modules/ce/components/referral/ReferralStatusChip';
import ProjectTypeChip from '@/modules/hmis/components/ProjectTypeChip';
import { parseAndFormatDate } from '@/modules/hmis/hmisUtil';
import { HmisEnums } from '@/types/gqlEnums';
import {
  CeReferralBaseTableFieldsFragment,
  CeReferralWithProjectFieldsFragment,
} from '@/types/gqlTypes';

export const REFERRAL_COLUMNS: Record<
  string,
  ColumnDef<CeReferralBaseTableFieldsFragment>
> = {
  client: {
    header: 'Client',
    render: (referral: CeReferralBaseTableFieldsFragment) =>
      referral.clientName,
    key: 'name',
    sticky: 'left',
  },
  opportunity: {
    header: 'Unit',
    key: 'unit',
    render: (referral: CeReferralBaseTableFieldsFragment) =>
      referral.opportunity?.name,
  },
  date: {
    header: 'Referral Date',
    key: 'date',
    render: (referral: CeReferralBaseTableFieldsFragment) =>
      parseAndFormatDate(referral.createdAt),
  },
  status: {
    header: 'Status',
    render: (referral: CeReferralBaseTableFieldsFragment) => (
      <ReferralStatusChip referral={referral} size='small' />
    ),
    key: 'status',
  },
  currentSteps: {
    key: 'currentSteps',
    header: 'Current Task',
    render: (referral) => {
      if (!referral.currentSteps || referral.currentSteps.length === 0) return;
      return (
        <CommonTruncatedList
          items={referral.currentSteps?.map((s) => s.name)}
        />
      );
    },
  },
  daysOnCurrentTask: {
    key: 'daysOnCurrentTask',
    header: 'Days on Current Task',
    render: ({ daysOnCurrentSteps }) => {
      if (isNil(daysOnCurrentSteps)) return; // no open steps
      if (daysOnCurrentSteps === 0) return '< 1 day';
      return `${daysOnCurrentSteps} day${daysOnCurrentSteps > 1 ? 's' : ''}`;
    },
  },
  currentTaskSwimlane: {
    key: 'currentTaskSwimlane',
    header: 'Assigned To',
    render: (referral) => {
      if (!referral.currentSteps || referral.currentSteps.length === 0) return;
      return (
        <CommonTruncatedList
          items={referral.currentSteps?.map((s) => s.swimlane)}
        />
      );
    },
  },
  currentTaskAssignees: {
    key: 'currentTaskAssignees',
    header: 'Current Task Assignees',
    render: (referral) => {
      if (!referral.currentSteps || referral.currentSteps.length === 0) return;

      const assignees = referral.currentSteps.flatMap((s) =>
        s.assignees.map((a) => a.name)
      );
      return <CommonTruncatedList items={assignees} />;
    },
  },
  referredBy: {
    header: 'Referred By',
    key: 'referredBy',
    render: (referral: CeReferralBaseTableFieldsFragment) =>
      referral.referredBy?.name,
  },
  origin: {
    header: 'Origin',
    key: 'origin',
    render: (referral: CeReferralBaseTableFieldsFragment) =>
      HmisEnums.CeReferralOrigin[referral.origin],
  },
  // TODO(#7321) - add column for sending project
};

export const REFERRAL_WITH_PROJECT_COLUMNS: {
  [key: string]: ColumnDef<CeReferralWithProjectFieldsFragment>;
} = {
  projectName: {
    header: 'Project Name',
    key: 'projectName',
    render: (referral: CeReferralWithProjectFieldsFragment) =>
      referral.targetProjectName,
  },
  projectType: {
    header: 'Project Type',
    key: 'projectType',
    render: (referral: CeReferralWithProjectFieldsFragment) => (
      <ProjectTypeChip projectType={referral.targetProjectType} />
    ),
  },
};
